import {
  forwardRef,
  useCallback,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { cn } from '../lib/cn';
import { useFieldContext } from './field-context';

/**
 * The slider.
 *
 * `<input type="range">` draws the operating system's own track and thumb —
 * a different shape on macOS, Windows and Android, none of them ours, and the
 * parts that can be styled need a different vendor pseudo-element per engine.
 * So the track is two divs and the thumb is a focusable `role="slider"`,
 * carrying the full ARIA contract the native control would have provided.
 *
 * Radix has a slider, and this is not built on it, deliberately:
 * `@radix-ui/react-slider` is not a dependency of this package, and the
 * behaviour a single-thumb slider needs — a keyboard model of eight keys and a
 * pointer position mapped onto a step grid — is smaller than the dependency.
 */

export interface SliderProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  /** Formats the value for the visible readout and for `aria-valuetext`. */
  formatValue?: (value: number) => string;
  /** Defaults to on whenever a formatter is supplied. */
  showValue?: boolean;
  /** Shown under the track at each end, e.g. the range being chosen within. */
  showBounds?: boolean;
  id?: string;
  /** Emitted on a hidden input so a native form submit carries the value. */
  name?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/** Page Up/Down and Shift move ten steps, as they do in the number field. */
const COARSE_MULTIPLIER = 10;

/** Digits after the decimal point, used to keep stepping off the float grid. */
function decimalsOf(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const text = String(Math.abs(value));
  const point = text.indexOf('.');
  return point === -1 ? 0 : text.length - point - 1;
}

export const Slider = forwardRef<HTMLDivElement, SliderProps>(function Slider(
  {
    value,
    onChange,
    min = 0,
    max = 100,
    step = 1,
    disabled,
    formatValue,
    showValue,
    showBounds = false,
    id,
    name,
    className,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
  },
  ref,
) {
  const field = useFieldContext();
  const generatedId = useId();
  const thumbId = field.id || id || generatedId;

  const trackRef = useRef<HTMLDivElement | null>(null);
  const thumbRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const span = max - min;
  const safeStep = step > 0 ? step : 1;

  const clamp = useCallback(
    (candidate: number): number => Math.min(max, Math.max(min, candidate)),
    [min, max],
  );

  /**
   * Snaps to the step grid measured from `min`, not from zero — a slider from
   * 5 to 100 in steps of 10 offers 5, 15, 25, not 10, 20, 30. The rounding at
   * the end is what stops 0.1-sized steps from accumulating float dust.
   */
  const snap = useCallback(
    (candidate: number): number => {
      const steps = Math.round((candidate - min) / safeStep);
      const decimals = Math.max(decimalsOf(safeStep), decimalsOf(min));
      const raw = min + steps * safeStep;
      return clamp(Number(raw.toFixed(decimals)));
    },
    [clamp, min, safeStep],
  );

  const commit = useCallback(
    (candidate: number): void => {
      const next = snap(candidate);
      if (next !== value) onChange(next);
    },
    [onChange, snap, value],
  );

  const commitFromPointer = useCallback(
    (clientX: number): void => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      // jsdom, and a track that has not been laid out yet, both report zero —
      // dividing by it would send the value to NaN.
      if (rect.width === 0) return;
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      commit(min + ratio * span);
    },
    [commit, min, span],
  );

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (disabled) return;
    event.preventDefault();
    // Capture keeps the drag alive when the pointer leaves the track, which is
    // most of a drag: people overshoot and come back.
    trackRef.current?.setPointerCapture?.(event.pointerId);
    setDragging(true);
    thumbRef.current?.focus();
    commitFromPointer(event.clientX);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (!dragging || disabled) return;
    commitFromPointer(event.clientX);
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (!dragging) return;
    setDragging(false);
    trackRef.current?.releasePointerCapture?.(event.pointerId);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    const multiplier = event.shiftKey ? COARSE_MULTIPLIER : 1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        commit(value + safeStep * multiplier);
        return;
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        commit(value - safeStep * multiplier);
        return;
      case 'PageUp':
        event.preventDefault();
        commit(value + safeStep * COARSE_MULTIPLIER);
        return;
      case 'PageDown':
        event.preventDefault();
        commit(value - safeStep * COARSE_MULTIPLIER);
        return;
      case 'Home':
        event.preventDefault();
        commit(min);
        return;
      case 'End':
        event.preventDefault();
        commit(max);
        return;
      default:
    }
  };

  const clamped = clamp(value);
  const ratio = span <= 0 ? 0 : (clamped - min) / span;
  const percent = `${ratio * 100}%`;
  const readout = formatValue ? formatValue(clamped) : String(clamped);
  const withReadout = showValue ?? Boolean(formatValue);

  return (
    <div ref={ref} className={cn('min-w-0', className)}>
      {withReadout ? (
        <div className="mb-1.5 text-base font-medium tabular-nums text-ink" aria-hidden>
          {readout}
        </div>
      ) : null}

      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={cn(
          'relative flex h-5 touch-none items-center',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        )}
      >
        <div className="h-1.5 w-full rounded-full bg-neutral-soft">
          <div
            className={cn(
              'h-full rounded-full bg-brand',
              // The fill animates only when the value moves on its own — under
              // a dragging finger a transition reads as lag. Reduced motion is
              // a stated preference, so it removes the transition entirely.
              !dragging && 'transition-[width] duration-100 motion-reduce:transition-none',
            )}
            style={{ width: percent }}
          />
        </div>

        <div
          ref={thumbRef}
          id={thumbId}
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy ?? (ariaLabel ? undefined : field.labelId)}
          aria-describedby={field.describedBy}
          aria-orientation="horizontal"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={clamped}
          aria-valuetext={formatValue ? formatValue(clamped) : undefined}
          aria-disabled={disabled || undefined}
          onKeyDown={onKeyDown}
          className={cn(
            'absolute h-4 w-4 -translate-x-1/2 rounded-full border-2 border-brand bg-surface shadow-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-1',
            !dragging && 'transition-[left] duration-100 motion-reduce:transition-none',
            disabled && 'border-line-strong',
          )}
          style={{ left: percent }}
        />
      </div>

      {showBounds ? (
        <div
          className="mt-1 flex justify-between text-caption tabular-nums text-ink-muted"
          aria-hidden
        >
          <span>{formatValue ? formatValue(min) : min}</span>
          <span>{formatValue ? formatValue(max) : max}</span>
        </div>
      ) : null}

      {name ? <input type="hidden" name={name} value={String(clamped)} /> : null}
    </div>
  );
});
